import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { Loader2 } from "lucide-react";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import EditIcon from "@mui/icons-material/Edit";
import axiosInstance from "../../../Config/axiosInstance";
import StatusBadge from "../../../Components/Badges/StatusBadge";
import AddSSM from "./AddSSM";
import EditSSM from "./EditSSM";
import "./SSM.css";

// mapare status -> variantă badge
const STATUS_VARIANTS = {
  Pending: "pending",
  Completed: "success",
  Critical: "critical",
  Active: "success",
  Expirat: "critical",
  Inactive: "critical",
  expired: "critical",
  active: "success",
  activ: "success",
};

const DEFAULT_PAGE_SIZE = 10;

const SSM = ({ employee, ssmRecords: initialRecords = [], onEditSuccess, onAddSuccess }) => {

  const { id } = useParams();

  // stări principale
  const [ssmRecords, setSsmRecords] = useState(initialRecords);
  const [ssmTypes, setSsmTypes] = useState({});
  const [providers, setProviders] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // modaluri și acțiuni
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showEditSSM, setShowEditSSM] = useState(false);

  // permisiuni
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canView, setCanView] = useState(false);

  // cheie pentru forțat refresh DataGrid
  const [refreshKey, setRefreshKey] = useState(0);

  // actualizează lista locală când vin date noi din props
  useEffect(() => {
    setSsmRecords(initialRecords);
  }, [initialRecords]);

  // verifică permisiunile din localStorage
  useEffect(() => {
    const requiredPath = "/employees/:id/ssm";
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    const checkPermission = (key) =>
      permissions.some((page) => {
        try {
          const pagePath = new URL(page.url).pathname;
          return pagePath === requiredPath && page[key] === true;
        } catch {
          return false;
        }
      });

    setCanCreate(checkPermission("can_create"));
    setCanEdit(checkPermission("can_edit"));
    setCanView(checkPermission("can_view"));
  }, []);

  const employeeId = id || employee?.id;

  // numele complet al angajatului
  const employeeName =
    employee?.full_name ||
    `${employee?.first_name || ""} ${employee?.last_name || ""}` ||
    "Employee";

  // convertire status
  const getStatusVariant = (status) => STATUS_VARIANTS[status] || "default";

  // calculează câte zile rămân / expirate
  const calculateNotification = (expireDate) => {
    if (!expireDate) return "-";
    const today = new Date();
    const exp = new Date(expireDate);
    const diffDays = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return `${Math.abs(diffDays)} days expired`;
    if (diffDays === 0) return "Expires today";
    return `${diffDays} days remaining`;
  };

  // fetch date SSM, tipuri și furnizori
  useEffect(() => {
    const fetchAllData = async () => {
      if (!employeeId) return;
      try {
        setLoading(true);
        setError(null);

        const [typesRes, provRes, ssmRes] = await Promise.all([
          axiosInstance.get("/type-ssm/"),
          axiosInstance.get("/suppliers/"),
          axiosInstance.get(`/employees/${employeeId}/ssm/`),
        ]);

        // transformăm listele în map pentru afișare rapidă
        const typesMap = typesRes.data.reduce((acc, t) => {
          acc[t.id] = t.type || "-";
          return acc;
        }, {});

        const provMap = provRes.data.reduce((acc, p) => {
          acc[p.id] = p.name || "-";
          return acc;
        }, {});

        setSsmTypes(typesMap);
        setProviders(provMap);
        setSsmRecords(ssmRes.data.ssm_records || []);
      } catch (err) {
        console.error("❌ Eroare la preluarea datelor SSM:", err);
        setError("Nu s-au putut prelua datele SSM.");
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [employeeId]);

  // debug logs
  useEffect(() => {
    console.log("SSM records updated:", ssmRecords.length);
  }, [ssmRecords]);

  useEffect(() => {
    console.log("Refresh triggered:", refreshKey);
  }, [refreshKey]);

  // refresh după add / edit
  const handleSSMDataUpdated = async () => {
    if (!employeeId) return;
    try {
      const response = await axiosInstance.get(`/employees/${employeeId}/ssm/`);
      const freshData = response.data.ssm_records || [];
      setSsmRecords(freshData);
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error("❌ Eroare la reîncărcarea datelor SSM:", err);
    }
  };

  // acțiuni modal add
  const handleOpenAdd = () => {
    if (canCreate) setIsAddOpen(true);
  };
  const handleCloseAdd = () => setIsAddOpen(false);

  // acțiuni modal edit
  const handleCloseEdit = () => {
    setEditingRecord(null);
    setShowEditSSM(false);
  };

  // transformă datele pentru DataGrid
  const filteredRows = useMemo(() => {
    return ssmRecords.map((r, idx) => ({
      id: r.id ?? idx,
      ssm_type: r.ssm_type || r.ssm_type_id || "",
      ssm_type_name: r.ssm_type_name || ssmTypes[r.ssm_type] || "-",
      provider_id: r.provider || "",
      provider_name: r.provider_name || providers[r.provider] || "-",
      execution_date: r.execution_date
        ? new Date(r.execution_date).toLocaleDateString("ro-RO")
        : "-",
      valid_date: r.valid_date
        ? new Date(r.valid_date).toLocaleDateString("ro-RO")
        : "-",
      expire_date: r.expire_date
        ? new Date(r.expire_date).toLocaleDateString("ro-RO")
        : "-",
      notification: calculateNotification(r.expire_date),
      status: r.status || "-",
    }));
  }, [ssmRecords, ssmTypes, providers]);

  // configurare columne DataGrid
  const columns = useMemo(() => {
    const handleEditClick = (record) => {
      if (!canEdit) return;

      // convertire "dd.mm.yyyy" -> ISO
      const parseToISO = (dateStr) => {
        if (!dateStr || dateStr === "-") return "";
        const [day, month, year] = dateStr.split(".");
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
      };

      setEditingRecord({
        ...record,
        execution_date: parseToISO(record.execution_date),
        valid_date: parseToISO(record.valid_date),
        expire_date: parseToISO(record.expire_date),
      });

      setShowEditSSM(true);
    };

    // columne comune
    const baseCols = [
      { field: "ssm_type_name", headerName: "Type", flex: 1, minWidth: 150 },
      { field: "execution_date", headerName: "Execution Date", flex: 0.8, minWidth: 130 },
      { field: "valid_date", headerName: "Valid From", flex: 0.8, minWidth: 130 },
      { field: "expire_date", headerName: "Valid Until", flex: 0.8, minWidth: 130 },

      {
        field: "notification",
        headerName: "Average Notification",
        flex: 1,
        minWidth: 160,
        // colorare condițională
        renderCell: (params) => {
          const value = params.value;
          if (!value || value === "-")
            return <span style={{ color: "#999", fontStyle: "italic" }}>–</span>;

          const isExpired = value.includes("expired");
          const isToday = value.includes("today");

          return (
            <span
              style={{
                color: isExpired ? "#d32f2f" : isToday ? "#f57c00" : "#2e7d32",
                fontWeight: 500,
              }}
            >
              {value}
            </span>
          );
        },
      },

      { field: "provider_name", headerName: "Provider", flex: 0.8, minWidth: 150 },

      {
        field: "status",
        headerName: "Status",
        flex: 0.8,
        minWidth: 120,
        // afișează badge colorat
        renderCell: (params) => {
          const statusText = params.value
            ? params.value.charAt(0).toUpperCase() + params.value.slice(1).toLowerCase()
            : "-";
          return <StatusBadge variant={getStatusVariant(params.value)} text={statusText} />;
        },
      },
    ];

    // coloană Edit dacă avem permisiune
    if (canEdit) {
      baseCols.push({
        field: "action",
        headerName: "Action",
        width: 100,
        sortable: false,
        renderCell: (params) => (
          <button
            className="ssm-edit-btn"
            onClick={() => handleEditClick(params.row)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#1976d2",
            }}
            title="Edit record"
          >
            <EditIcon fontSize="small" />
          </button>
        ),
      });
    }

    return baseCols;
  }, [canEdit]);

  // acces interzis
  if (!canView) {
    return (
      <div className="page-ssm">
        <div className="ssm-empty">
          <MedicalServicesOutlinedIcon className="ssm-empty-icon" sx={{ fontSize: 48 }} />
          <h3>Access Restricted</h3>
          <p>You do not have permission to view SSM records for this employee.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-ssm">
      {loading ? (
        // loader pe încărcare
        <div className="ssm-loading">
          <Loader2 className="ssm-loader-icon" />
          <span>Se încarcă datele SSM...</span>
        </div>
      ) : error ? (
        // eroare la fetch
        <div className="ssm-empty">
          <MedicalServicesOutlinedIcon className="ssm-empty-icon" sx={{ fontSize: 48 }} />
          <h3>Eroare</h3>
          <p>{error}</p>
        </div>
      ) : ssmRecords.length === 0 ? (
        // nu există înregistrări
        <div className="ssm-empty">
          <MedicalServicesOutlinedIcon className="ssm-empty-icon" sx={{ fontSize: 48 }} />
          <h3>No SSM Records Found</h3>
          <p>No SSM records have been created for {employeeName} yet.</p>

          {canCreate && (
            <button className="ssm-create-btn" onClick={handleOpenAdd}>
              Create New Record
            </button>
          )}

          {canCreate && (
            <AddSSM
              isOpen={isAddOpen}
              onClose={handleCloseAdd}
              onSuccess={() => {
                handleSSMDataUpdated();
                if (onAddSuccess) onAddSuccess();
                handleCloseAdd();
              }}
              employerData={employee}
            />
          )}
        </div>
      ) : (
        // tabelul cu date
        <div className="table-container">
          <DataGrid
            key={refreshKey}
            rows={filteredRows}
            columns={columns}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 15, 20, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: DEFAULT_PAGE_SIZE, page: 0 } },
            }}
            rowHeight={40}
            sx={{
              borderRadius: "8px",
              "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
              "& .MuiDataGrid-columnHeaders": { fontWeight: "700", color: "#111" },
            }}
          />

          {/* modal add */}
          {canCreate && (
            <AddSSM
              isOpen={isAddOpen}
              onClose={handleCloseAdd}
              onSuccess={() => {
                handleSSMDataUpdated();
                if (onAddSuccess) onAddSuccess();
                handleCloseAdd();
              }}
              employerData={employee}
            />
          )}

          {/* modal edit */}
          {canEdit && (
            <EditSSM
              isOpen={showEditSSM}
              onClose={handleCloseEdit}
              recordData={editingRecord}
              employerData={employee}
              onSuccess={() => {
                handleSSMDataUpdated();
                handleCloseEdit();
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default SSM;
